import { formatMoney } from '@aeris/money';
import { formatDocumentNumber } from "./document-number.js";
import { customerFacts, infoBlocks, } from "./info-lines.js";
import { documentTemplate, footerBlocks, headerBlocks, itemLocation, itemName, styleOf, templateOr, } from "./receipt-template.js";
/** `05/08/2026 14:32`, sem `Intl` e sem surpresa de fuso. */
const stamp = (at) => {
    const pad = (value) => String(value).padStart(2, '0');
    return (`${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${String(at.getFullYear())}` +
        ` ${pad(at.getHours())}:${pad(at.getMinutes())}`);
};
/** Só o dia: a hora do vencimento não interessa a ninguém no balcão. */
const day = (at) => {
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${String(at.getFullYear())}`;
};
/**
 * "1 dia", "7 dias" — como o papel antigo dizia a validade (F161). Contado em
 * dias do calendário, do dia da emissão ao último dia válido: um orçamento de
 * hoje válido até amanhã vale 1 dia.
 */
export const validityDays = (issuedAt, validUntil) => {
    const startOf = (at) => new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
    const days = Math.max(0, Math.round((startOf(validUntil) - startOf(issuedAt)) / 86_400_000));
    return `${String(days)} ${days === 1 ? 'dia' : 'dias'}`;
};
export const quoteFor = (input) => {
    const model = templateOr(input.template);
    const doc = documentTemplate(input.template, 'quote');
    const blocks = headerBlocks({
        company: input.company,
        branchName: input.branchName,
        template: input.template,
    });
    blocks.push({ kind: 'rule' }, { kind: 'text', text: 'ORÇAMENTO', align: 'center', bold: true });
    /*
     * A recusa, dita antes dos valores.
     *
     * Um papel com itens, preços e um total é indistinguível de um recibo para
     * quem não trabalha com isso. Um cliente que acha que já pagou volta ao caixa
     * achando que vai só retirar, e a conversa que segue é ruim para as duas
     * partes. A frase é da loja (F149); o lugar dela, não.
     */
    if (doc.notice !== null) {
        blocks.push({ kind: 'text', text: doc.notice, align: 'center' });
    }
    blocks.push({ kind: 'rule' });
    /*
     * As linhas de dados, na ordem que a loja escolheu no editor (F160).
     *
     * O número continua GRANDE onde quer que a loja o ponha: é lido em voz alta
     * — "traz o vinte e quatro" é como ele atravessa a loja —, e precisa ser
     * legível de longe, na mão de alguém, sob a luz que a loja tiver.
     */
    blocks.push(...infoBlocks(model, {
        number: {
            kind: 'text',
            text: `Nº ${formatDocumentNumber(input.number)}`,
            big: true,
            bold: true,
        },
        date: { kind: 'pair', label: 'Data', value: stamp(input.issuedAt) },
        validUntil: input.validUntil === null
            ? null
            : model.quoteValidity === 'days'
                ? {
                    kind: 'pair',
                    label: 'Validade',
                    value: validityDays(input.issuedAt, input.validUntil),
                }
                : {
                    kind: 'pair',
                    label: 'Válido até',
                    value: day(input.validUntil),
                },
        customer: { kind: 'pair', label: 'Cliente', value: input.customerName },
        ...customerFacts(model, input.customer, input.customerName),
        salesperson: input.salespersonName === null
            ? null
            : { kind: 'pair', label: 'Vendedor', value: input.salespersonName },
    }));
    blocks.push({ kind: 'rule' });
    // Duas linhas por item, pelo mesmo motivo do recibo: o nome do produto é
    // maior que qualquer bobina, e a conta fica embaixo do que ela multiplicou.
    for (const line of input.lines) {
        blocks.push({ kind: 'text', text: itemName(model, line) });
        const location = itemLocation(model, line);
        if (location !== null)
            blocks.push({ kind: 'text', text: location });
        blocks.push({
            kind: 'pair',
            label: `  ${line.quantity} ${line.unit} x ${formatMoney(line.unitPrice, { symbol: false })}`,
            value: formatMoney(line.lineTotal, { symbol: false }),
        });
        // O desconto do item, embaixo da conta dele (F161): o papel antigo tinha a
        // coluna "Des/Acr", e o cliente confere item por item.
        if (model.lines.showLineDiscount &&
            line.discount !== undefined &&
            line.discount.units !== 0n) {
            blocks.push({
                kind: 'pair',
                label: '  desconto',
                value: `-${formatMoney(line.discount, { symbol: false })}`,
            });
        }
    }
    blocks.push({ kind: 'rule' }, {
        kind: 'pair',
        label: 'Subtotal',
        value: formatMoney(input.subtotal, { symbol: false }),
    });
    if (input.discount.units !== 0n) {
        blocks.push({
            kind: 'pair',
            label: 'Desconto',
            value: `-${formatMoney(input.discount, { symbol: false })}`,
        });
    }
    blocks.push({ kind: 'text', text: 'TOTAL', big: true, bold: true }, {
        kind: 'text',
        text: formatMoney(input.total),
        align: 'right',
        big: true,
        bold: true,
    }, { kind: 'rule' }, {
        kind: 'text',
        text: input.reservesStock
            ? 'Mercadoria separada. Leve este papel ao caixa para retirar.'
            : 'Preço garantido até a data acima. A mercadoria não está separada.',
        align: 'center',
    }, { kind: 'feed', lines: 1 });
    /*
     * O NÚMERO no código de barras, nunca o uuid.
     *
     * São 36 caracteres que o CODE128 não põe de pé em 58mm de forma legível — e
     * ninguém procura um orçamento pelo uuid. O caixa procura pelo número, que é
     * o que o cliente fala e o que a tela busca.
     */
    if (doc.showBarcode) {
        blocks.push({
            kind: 'barcode',
            data: String(input.number),
            symbology: 'code128',
        });
    }
    blocks.push({ kind: 'feed', lines: 1 });
    if (doc.showDocumentId) {
        blocks.push({ kind: 'text', text: input.documentId, align: 'center' });
    }
    blocks.push(...footerBlocks(input.template, 'quote'), { kind: 'cut' });
    return {
        kind: 'quote',
        title: `Orçamento nº ${formatDocumentNumber(input.number)}`,
        blocks,
        style: styleOf(input.template),
    };
};
//# sourceMappingURL=quote.js.map